import useSWR from 'swr'
import { resolveUrl } from '../../api'
import { getLogger } from '../../logger'
import { Channel, ChannelPane, PlayableList } from 'common/types'
import { PageLoading } from '../Loading/Loading'
import { OverlayInfoText } from './OverlayInfo'
import { unwrapChannelPane } from '../../utils'
import { Player } from './Player'
import { useMemo } from 'react'
import { LayoutStyleObjectFit } from '../../../../src/types/layout.types'

const log = getLogger('channel')

interface ChannelPlayerProps {
  channelId?: string
  channelData?: Channel
  isPreview?: boolean
  isBackgroundPane?: boolean
}

interface PaneViewerProps {
  pane: ChannelPane
  isBackgroundPane?: boolean
}

export const BackgroundPane = () => <div className="w-full h-full" />

export const PaneViewer = ({ pane, isBackgroundPane = false }: PaneViewerProps) => {
  const playables = useMemo(() => unwrapChannelPane(pane), [pane])

  if (playables.length === 0) {
    return null
  }
  const objectFit = pane?.style?.objectFit || LayoutStyleObjectFit.FILL

  return <Player playables={playables} objectFit={objectFit} />
}

export const ChannelPlayer = ({ channelId, channelData, isPreview }: ChannelPlayerProps) => {
  const { data: fetchedChannel, error } = useSWR<Channel>(
    channelData ? null : resolveUrl(`channel/${channelId}`, isPreview ? { preview: true } : {}),
    {
      refreshInterval: 0,
      revalidateOnFocus: false,
    }
  )
  const channel = channelData || fetchedChannel

  // Only show error if we have no cached data to display
  if (error && !channel) {
    return <OverlayInfoText text={error.message || 'Error loading channel'} />
  }

  if (!channel) return <PageLoading />

  const { panes } = channel

  log(`Rendering channel player for channel ${channelId} with panes`, panes)

  return panes.map((pane) => {
    const {
      x_percent = 0,
      y_percent = 0,
      width_percent = 100,
      height_percent = 100,
      z_index = 1,
      position = 'absolute',
      borderWidth = 0,
      borderRadius = 0,
      borderColor = '#000000',
      backgroundColor,
      opacity = 1,
      padding = 0,
    } = pane?.style || {}

    const isBackgroundPane = z_index === -1

    // Outer pane style - handles position, size, padding, and background
    const paneStyle: React.CSSProperties = {
      position: position,
      left: `${x_percent}%`,
      top: `${y_percent}%`,
      width: `${width_percent}%`,
      height: `${height_percent}%`,
      zIndex: z_index,
      // Only apply background color for background panes
      backgroundColor: isBackgroundPane && backgroundColor ? backgroundColor : 'transparent',
      opacity: opacity,
      padding: `${padding}%`,
      overflow: 'hidden',
      boxSizing: 'border-box',
    }

    // Inner content style - handles border (inside the padding)
    const innerStyle: React.CSSProperties = {
      width: '100%',
      height: '100%',
      borderWidth: `${borderWidth}px`,
      borderStyle: borderWidth > 0 ? 'solid' : 'none',
      borderColor: borderColor,
      borderRadius: `${borderRadius}px`,
      overflow: 'hidden',
      boxSizing: 'border-box',
    }

    return (
      <div key={pane.id} style={paneStyle}>
        <div style={innerStyle}>
          <PaneViewer pane={pane} isBackgroundPane={isBackgroundPane} />
        </div>
      </div>
    )
  })
}
